import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth, roleHome } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ScanLine, Eye, EyeOff } from "lucide-react";

export default function LoginPage() {
  const { user, login, error } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const requestedFrom = location.state?.from;

  useEffect(() => {
    if (user && typeof user === "object") {
      navigate(requestedFrom || roleHome(user.role), { replace: true });
    }
  }, [user, navigate, requestedFrom]);

  if (user && typeof user === "object") {
    return <Navigate to={requestedFrom || roleHome(user.role)} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const authed = await login(email.trim(), password);
    setSubmitting(false);
    if (authed) navigate(requestedFrom || roleHome(authed.role), { replace: true });
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left brand side (desktop only) */}
      <div className="hidden lg:flex flex-col justify-between p-12 border-r border-white/[0.06] bg-card/40">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-blue-500 grid place-items-center">
            <ScanLine className="w-5 h-5 text-primary-foreground" />
          </div>
          <div>
            <div className="font-heading text-xl font-bold">1AI Review</div>
            <div className="text-xs text-muted-foreground tracking-widest uppercase">
              1AI Agency
            </div>
          </div>
        </div>
        <div className="max-w-md">
          <h1 className="font-heading text-4xl font-bold tracking-tight">
            Mehr Google-Bewertungen für lokale Unternehmen.
          </h1>
          <p className="text-muted-foreground mt-4 text-base leading-relaxed">
            NFC-Chips und QR-Sticker mit deiner eigenen Custom-URL. Jede Berührung landet
            direkt beim Google-Bewertungsdialog — messbar, zuverlässig und blitzschnell.
          </p>
        </div>
        <div className="text-xs text-muted-foreground">
          © {new Date().getFullYear()} 1AI Agency
        </div>
      </div>

      {/* Right login form */}
      <div className="flex items-center justify-center px-5 py-10">
        <div className="w-full max-w-sm card-surface p-7">
          <div className="lg:hidden mb-6 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-blue-500 grid place-items-center">
              <ScanLine className="w-5 h-5 text-primary-foreground" />
            </div>
            <div>
              <div className="font-heading font-bold">1AI Review</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                1AI Agency
              </div>
            </div>
          </div>
          <h2 className="font-heading text-2xl font-semibold">Admin-Anmeldung</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Bitte melde dich an, um fortzufahren.
          </p>

          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">E-Mail</Label>
              <Input
                id="email"
                type="email"
                required
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                placeholder="admin@1ai-agency.com"
                className="bg-background/40"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Passwort</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPw ? "text" : "password"}
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  className="bg-background/40 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground hover:text-foreground"
                  data-testid="login-toggle-password"
                  aria-label="Passwort anzeigen"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {error ? (
              <div
                data-testid="login-error"
                className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md px-3 py-2"
              >
                {error}
              </div>
            ) : null}

            <Button
              type="submit"
              className="w-full h-11 rounded-lg font-medium"
              disabled={submitting}
              data-testid="login-submit-button"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Anmelden
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
